'use client'
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { getPreviewProducts } from "@/server/server-actions"
import { ImageSide, PreviewProductItemType, ProductItemCategory } from "@/utils/APIRouteTypes"
import { useEffect, useState } from "react"
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../ui/carousel"
import { NonUrlContextProvider, useNonUrlContext } from "@/contexts/NonUrlContext"
import AggregatedImage from "./aggregatedImage"
import AggregatedImage2 from "./aggregatedImage2"
import { cn } from "@/lib/utils"
import { useCustomImageContext } from "@/contexts/CustomImageContext"
import { useParams } from "next/navigation"
import ProductPrice from "../detailComponents/productPrice"
import Link from "next/link"
import { Properties } from "@/utils/properties"
import { ShoppingCartIcon } from "lucide-react"

type Props = {
    category: ProductItemCategory
}

export default function ViewOnOtherItemModal(props: Props) {

    const [previewProducts, setPreviewProducts] = useState<PreviewProductItemType[] | null>([])
    const [open, setOpen] = useState(false)
    const [customImageContext, _] = useCustomImageContext()
    const params = useParams<{ id: string }>()
    const nonUrlCtx = useNonUrlContext()
    const isFront = customImageContext.backImages.length < customImageContext.frontImages.length
    const [currentSlide, setCurrentSlide] = useState(0)
    useEffect(() => {
        if (open && (!previewProducts || previewProducts.length == 0)) {
            try {
                getPreviewProducts(props.category).then((res) => {
                    setPreviewProducts(res.results.filter(item => (item.colors.findIndex(x => x.colorType === nonUrlCtx.selectedColor?.colorType) !== -1) && item.slug !== params.id && item.colors[0].images.findIndex(x => x.side == (isFront ? ImageSide.FRONT : ImageSide.BACK)) !== -1))
                })
            }
            catch (e) {
                console.error(e)
                setPreviewProducts(null)
            }
        }
    }, [open])

    const updateSLide = (n: number) => {
        setCurrentSlide(n)
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {customImageContext.frontImages.length + customImageContext.backImages.length > 0 && <DialogTrigger asChild>
                <Button className="rounded-full w-full mb-6">View this design on other products</Button>
            </DialogTrigger>}
            <DialogContent className="max-w-[425px]">
                <DialogHeader className=" z-[999]">
                    <DialogTitle>Preview on Other Products</DialogTitle>
                </DialogHeader>
                <div className="flex items-center justify-center min-h-96 flex-col relative ">
                    {previewProducts?.length == 0 && <span className="loading loading-spinner loading-md"></span>}
                    <Carousel >
                        <CarouselContent setNewSlide={updateSLide}>
                            {previewProducts?.map((item, index) => {
                                const colorToUse = item.colors.find(x => x.name.toLowerCase() == nonUrlCtx.selectedColor?.name.toLowerCase()) || item.colors.find(x => x.colorType == nonUrlCtx.selectedColor?.colorType) || item.colors[0]
                                return (
                                    <CarouselItem key={index} className=" relative overflow-clip w-full max-w-full">
                                        <Link href={`${Properties.routes.PRODUCT_LIST}/${item.slug}?color=${colorToUse.name}&context=${encodeURIComponent(JSON.stringify(customImageContext))}`} className="w-full max-w-full ">
                                            <NonUrlContextProvider sizes={[]} colors={[colorToUse]} sampleImages={[]}>
                                                <div className='overflow-clip h-[500px]'>
                                                    {item.boundingBoxBack && <AggregatedImage
                                                        side={isFront ? "front" : 'back'}
                                                        imgSrc={colorToUse.images.find(x => x.side == (isFront ? ImageSide.FRONT : ImageSide.BACK))?.image || ''}
                                                        boundingBox={isFront ? item.boundingBoxFront : item.boundingBoxBack}
                                                        imgHeight={800}
                                                        imgWidth={600}
                                                        tshirtAltText='Preview'
                                                        className='-my-2'
                                                    />}
                                                </div>
                                            </NonUrlContextProvider>
                                        </Link>
                                    </CarouselItem>
                                )
                            })}
                        </CarouselContent>
                        <CarouselPrevious />
                        <CarouselNext />
                    </Carousel>
                    {previewProducts?.[currentSlide] &&
                        <DialogFooter className="w-full">
                            <div className="w-full flex overflow-hidden items-center">
                                <div className="flex flex-col justify-between w-full py-2 items-start">
                                    <div className="flex items-center">
                                        <p className='font-semibold text-lg line-clamp-1'>{previewProducts?.[currentSlide]?.name}</p>
                                    </div>
                                    <div className={cn('mt-0 flex-row items-center flex text-xs sm:text-sm')}>
                                        <p className={cn(' font-normal flex p-0 sm:text-xl text-lg me-1')}>
                                            <span className='text-sm self-start justify-self-start font-light mt-1'>
                                                ₹
                                            </span>
                                            {previewProducts?.[currentSlide].price}
                                        </p>
                                        <div>
                                            <p className="my-0 text-xs leading-3 text-red-500">
                                                ({Math.round(((1 - (previewProducts?.[currentSlide].price / previewProducts?.[currentSlide].MRP)) * 100))}% OFF)
                                            </p>
                                            <p className="my-0 leading-3 text-xs text-slate-500 sm:text-sm font-light">
                                                M.R.P:
                                                <span className=" line-through ">
                                                    &nbsp;₹{previewProducts?.[currentSlide].MRP}
                                                </span>
                                                &nbsp;
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <Link className="!m-0 !p-0 h-fit rounded-full bg-black text-white hover:bg-gray-800" href={`${Properties.routes.PRODUCT_LIST}/${previewProducts?.[currentSlide].slug}?color=${nonUrlCtx.selectedColor?.name}&context=${encodeURIComponent(JSON.stringify(customImageContext))}`}>
                                    <ShoppingCartIcon className="size-6 m-2" />
                                </Link>
                            </div>
                        </DialogFooter>
                    }
                </div>

            </DialogContent>
        </Dialog>
    )
}
